import { Gauge, MonitorSmartphone, MousePointerClick, X, Check, Zap, Shield, TrendingUp, Search, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";

const BEFORE_ITEMS = [
  { label: "Ladezeit 6–12 Sekunden", detail: "Besucher springen ab, bevor die Seite lädt" },
  { label: "Kein mobiles Design", detail: "70% der Besucher kommen vom Smartphone" },
  { label: "Veraltete Optik & Templates", detail: "Kein Vertrauen, kein Wiedererkennungswert" },
  { label: "Kaum Anfragen über die Webseite", detail: "Kein klarer Call-to-Action, keine Conversion" },
  { label: "Unsichtbar bei Google", detail: "Keine lokale SEO, kein Google Business" },
];

const AFTER_ITEMS = [
  { label: "Ladezeit unter 1 Sekunde", detail: "Google PageSpeed 98+ — Top-Ranking & UX" },
  { label: "100% Mobile-First Design", detail: "Perfekt auf jedem Smartphone & Tablet" },
  { label: "Individuelles Premium-Design", detail: "Unverwechselbare Markenidentität die verkauft" },
  { label: "Bis zu +140% mehr Anfragen", detail: "Conversion-optimierte Struktur & CTAs" },
  { label: "Lokal auf Seite 1 bei Google", detail: "Gezieltes Local SEO & Google Business Setup" },
];

const HIGHLIGHTS = [
  {
    icon: Gauge,
    stat: "98/100",
    label: "Google PageSpeed",
    description: "Optimierte Assets, sauberer Code und Ladezeiten unter einer Sekunde — Google und Ihre Besucher lieben das.",
  },
  {
    icon: MonitorSmartphone,
    stat: "100%",
    label: "Mobile-First",
    description: "Über 70% Ihrer Kunden kommen mobil. Jede Seite wird zuerst für das Smartphone entworfen und optimiert.",
  },
  {
    icon: MousePointerClick,
    stat: "+140%",
    label: "Mehr Anfragen möglich",
    description: "Conversion-fokussierte Struktur: klare Handlungsaufforderungen, Vertrauen und ein Design, das verkauft.",
  },
];

export const Showcase = () => {
  return (
    <section
      id="vergleich"
      data-testid="showcase-section"
      className="relative bg-[#0A0A0A] py-24 lg:py-32 border-t border-white/10 overflow-hidden"
    >
      {/* Background glow */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className="absolute top-1/2 left-1/2 h-[600px] w-[800px] -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{ background: "radial-gradient(circle, rgba(197, 160, 89, 0.05) 0%, transparent 70%)" }}
        />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        {/* Header */}
        <Reveal>
          <div className="mb-16 max-w-2xl">
            <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-[#C5A059]">
              VORHER / NACHHER
            </p>
            <h2 className="font-heading text-3xl font-semibold leading-tight tracking-tight text-white sm:text-4xl">
              Das Problem — und die{" "}
              <span className="text-[#C5A059]">Wango Media Lösung.</span>
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
              Veraltete Webseiten kosten täglich Kunden. Wir machen Schluss damit — mit einer Website, die wirklich für Sie arbeitet.
            </p>
          </div>
        </Reveal>

        {/* Side-by-Side Comparison Cards */}
        <Reveal delay={100}>
          <div className="grid gap-6 md:grid-cols-2">

            {/* BEFORE Card */}
            <div
              data-testid="before-card"
              className="group relative rounded-2xl border border-red-500/20 bg-[#121212] p-7 sm:p-8 transition-all duration-300 hover:border-red-500/35"
            >
              {/* Card header */}
              <div className="mb-6 flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-red-500/30 bg-red-500/10">
                  <X className="h-6 w-6 text-red-400" />
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-red-300">Status Quo</p>
                  <h3 className="font-heading text-lg font-semibold text-white">Die veraltete Webseite</h3>
                </div>
                <span className="ml-auto rounded-full border border-red-500/30 bg-red-500/10 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-red-400">
                  Verliert Kunden
                </span>
              </div>

              {/* Mock browser */}
              <div className="mb-6 overflow-hidden rounded-xl border border-white/10 bg-[#0A0A0A]">
                <div className="flex items-center gap-2 border-b border-white/10 bg-gray-900 px-3 py-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-red-500/70" />
                  <span className="h-2.5 w-2.5 rounded-full bg-yellow-500/70" />
                  <span className="h-2.5 w-2.5 rounded-full bg-gray-600" />
                  <div className="ml-2 flex-1 rounded bg-gray-800 px-2 py-0.5 text-[10px] text-red-400">
                    ⚠ http://ihre-firma.de — 7,4s Ladezeit
                  </div>
                </div>
                <div className="bg-gray-100 p-4 font-serif">
                  <div className="mb-3 flex items-center justify-between border-b-2 border-blue-900 pb-2">
                    <span className="text-sm font-bold italic text-blue-900">Ihre Firma GmbH</span>
                    <div className="hidden items-center gap-2 sm:flex">
                      {["Home", "Über uns", "Gästebuch"].map(l => (
                        <span key={l} className="text-[10px] text-blue-700 underline">{l}</span>
                      ))}
                    </div>
                  </div>
                  <div className="inline-block bg-yellow-200 border border-yellow-400 px-2 py-0.5 text-[10px] text-yellow-900 mb-2">
                    ⚠️ Diese Seite ist auf Smartphones nicht lesbar!
                  </div>
                  <p className="text-sm font-bold text-blue-950 underline">Willkommen auf unserer Homepage!!!</p>
                  <p className="mt-1 text-[10px] text-gray-600 leading-tight">Rufen Sie uns an Mo-Fr 9-12 Uhr oder schicken Sie ein Fax.</p>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="bg-blue-800 px-2 py-1 text-[10px] font-bold text-white">HIER KLICKEN &gt;&gt;</span>
                    <span className="text-[9px] text-gray-500">Update: 2013 | Besucher: 004812</span>
                  </div>
                </div>
              </div>

              {/* Problem list */}
              <div className="space-y-3">
                {BEFORE_ITEMS.map((item) => (
                  <div key={item.label} className="flex items-start gap-3 rounded-xl border border-red-500/15 bg-red-500/5 px-4 py-3">
                    <X className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />
                    <div>
                      <p className="text-sm font-medium text-red-300">{item.label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{item.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* AFTER Card */}
            <div
              data-testid="after-card"
              className="group relative rounded-2xl border border-[#C5A059]/30 bg-[#121212] p-7 sm:p-8 shadow-xl shadow-[#C5A059]/5 transition-all duration-300 hover:border-[#C5A059]/50 hover:shadow-[#C5A059]/10"
            >
              {/* Subtle glow bg */}
              <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-br from-[#C5A059]/5 via-transparent to-transparent" />

              {/* Card header */}
              <div className="relative mb-6 flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-[#C5A059]/40 bg-[#C5A059]/15">
                  <Check className="h-6 w-6 text-[#C5A059]" />
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-[#C5A059]">Wango Media</p>
                  <h3 className="font-heading text-lg font-semibold text-white">Die Premium-Lösung</h3>
                </div>
                <span className="ml-auto rounded-full border border-[#C5A059]/40 bg-[#C5A059]/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-[#C5A059]">
                  Gewinnt Kunden
                </span>
              </div>

              {/* Mock browser – modern dark design */}
              <div className="relative mb-6 overflow-hidden rounded-xl border border-white/15 bg-[#0A0A0A] shadow-lg">
                <div className="flex items-center justify-between border-b border-white/10 bg-[#121212] px-3 py-2">
                  <div className="flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-emerald-500/80" />
                    <span className="h-2.5 w-2.5 rounded-full bg-yellow-500/60" />
                    <span className="h-2.5 w-2.5 rounded-full bg-rose-500/60" />
                  </div>
                  <div className="flex items-center gap-1.5 rounded-md bg-white/5 border border-white/10 px-2.5 py-0.5 text-[10px] text-emerald-400">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    https://ihre-firma.de · 0,8s · ✅ DSGVO
                  </div>
                  <span className="text-[10px] font-semibold text-[#C5A059] bg-[#C5A059]/10 border border-[#C5A059]/30 rounded px-2 py-0.5">
                    WANGO MEDIA
                  </span>
                </div>
                <div className="bg-gradient-to-br from-[#121212] to-[#0D0D0D] p-4">
                  <div className="flex items-center justify-between border-b border-white/10 pb-3 mb-3">
                    <div className="flex items-center gap-2">
                      <div className="h-7 w-7 rounded-lg bg-[#C5A059] flex items-center justify-center text-[#0A0A0A] text-xs font-bold">W</div>
                      <span className="text-xs font-bold text-white tracking-wide">IHRE FIRMA</span>
                    </div>
                    <span className="rounded-full bg-[#C5A059] px-3 py-1 text-[9px] font-bold text-[#0A0A0A]">
                      Erstgespräch →
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="inline-flex items-center gap-1.5 rounded-full bg-[#C5A059]/10 border border-[#C5A059]/30 px-2.5 py-0.5 text-[9px] font-semibold text-[#C5A059]">
                      ✦ #1 Webdesign Lüdenscheid
                    </div>
                    <p className="text-sm font-bold text-white leading-snug">
                      Mehr Kunden mit <span className="text-[#C5A059]">messbarem Ergebnis.</span>
                    </p>
                    <div className="grid grid-cols-3 gap-1.5 pt-1">
                      {["98/100 Speed", "Mobile 100%", "+140% Leads"].map(t => (
                        <div key={t} className="rounded-lg border border-white/10 bg-white/5 py-1.5 text-center text-[9px] font-medium text-[#C5A059]">{t}</div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Solution list */}
              <div className="relative space-y-3">
                {AFTER_ITEMS.map((item) => (
                  <div key={item.label} className="flex items-start gap-3 rounded-xl border border-[#C5A059]/20 bg-[#C5A059]/5 px-4 py-3 transition-colors hover:border-[#C5A059]/35">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#C5A059]" />
                    <div>
                      <p className="text-sm font-medium text-white">{item.label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{item.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>

        {/* Stats Row */}
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {HIGHLIGHTS.map((item, i) => (
            <Reveal key={item.label} delay={i * 120} className="h-full">
              <div
                data-testid={`highlight-card-${i + 1}`}
                className="group flex h-full flex-col rounded-2xl border border-white/10 bg-[#121212] p-7 transition-all duration-300 hover:-translate-y-1 hover:border-[#C5A059]/50"
              >
                <item.icon className="h-6 w-6 text-[#C5A059]" />
                <p className="mt-5 font-heading text-3xl font-semibold tracking-tight text-white">
                  {item.stat}
                </p>
                <p className="mt-1 text-sm font-semibold text-[#C5A059]">{item.label}</p>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {item.description}
                </p>
              </div>
            </Reveal>
          ))}
        </div>

        {/* CTA below */}
        <Reveal delay={200}>
          <div className="mt-14 rounded-2xl border border-[#C5A059]/20 bg-gradient-to-br from-[#C5A059]/8 via-[#121212] to-[#121212] p-8 sm:p-10 text-center">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#C5A059] mb-3">Bereit für den nächsten Schritt?</p>
            <h3 className="font-heading text-2xl sm:text-3xl font-semibold text-white mb-4">
              Ihre neue Webseite wartet auf Sie.
            </h3>
            <p className="text-base text-muted-foreground mb-8 max-w-xl mx-auto">
              Kostenloser Ersttermin, Festpreis, keine versteckten Kosten — und eine Webseite, die echte Ergebnisse liefert.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="/#kontakt"
                className="group inline-flex items-center gap-2 rounded-full bg-[#C5A059] px-8 py-4 text-sm font-semibold text-[#0A0A0A] shadow-lg shadow-[#C5A059]/20 transition-all duration-200 hover:bg-[#b08e4c]"
              >
                Projekt unverbindlich anfragen
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <a
                href="https://wa.me/491748367114"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Direkt per WhatsApp anfragen"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-8 py-4 text-sm font-semibold text-white transition-all duration-200 hover:border-emerald-500/50 hover:text-emerald-400"
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                Direkt per WhatsApp
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
